import { useCallback, useEffect, useRef, useState } from "react";

import { fetchSearchMetadata } from "./search-api";
import { SearchPageMetadata } from "./search-metadata.types";

export interface UseSearchMetadataResult {
  metadata: SearchPageMetadata | null;
  isLoading: boolean;
  error: string | null;
  reload(): void;
}

/**
 * Loads the search page rendering metadata (tabs, fields, validation rules,
 * result columns). Backed by the mock API until the backend endpoint exists.
 */
export const useSearchMetadata = (): UseSearchMetadataResult => {
  const [metadata, setMetadata] = useState<SearchPageMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);

    fetchSearchMetadata()
      .then((payload) => {
        if (requestId !== requestIdRef.current) return;
        setMetadata(payload);
      })
      .catch((err: unknown) => {
        if (requestId !== requestIdRef.current) return;
        setMetadata(null);
        setError(
          err instanceof Error ? err.message : "Failed to load search metadata",
        );
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
    return () => {
      // Invalidate in-flight requests on unmount
      requestIdRef.current += 1;
    };
  }, [load]);

  return { metadata, isLoading, error, reload: load };
};
